import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Plus, Send, CheckCircle2, Loader2, RefreshCw, AlertTriangle, X, Zap, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pendiente: { label: 'Pendiente', cls: 'text-amber-400 bg-amber-500/10 border-amber-500/25' },
  pagado:    { label: 'Pagado',    cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25' },
  vencida:   { label: 'Vencida',   cls: 'text-red-400 bg-red-500/10 border-red-500/25' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const isOverdue = (inv) => inv.status === 'pendiente' && inv.due_date_iso < new Date().toISOString().split('T')[0];

function NewInvoiceModal({ onClose, onSave }) {
  const today = new Date(); today.setDate(today.getDate() + 30);
  const defaultDue = today.toISOString().split('T')[0];
  const [form, setForm] = useState({ client_name: '', client_phone: '', client_email: '', concept: '', amount_usd: '', due_date_iso: defaultDue, notes: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); if (!form.client_name || !form.concept || !form.amount_usd) return; onSave({ ...form, amount_usd: parseFloat(form.amount_usd) }); };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-card w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="text-text-heading font-semibold">Nueva Factura</h3><button onClick={onClose}><X className="w-4 h-4 text-text-muted" /></button></div>
        <form onSubmit={submit} className="space-y-3">
          {[['Cliente *', 'client_name'], ['Teléfono WA', 'client_phone'], ['Email', 'client_email'], ['Concepto *', 'concept']].map(([label, key]) => (
            <div key={key}>
              <label className="text-[11px] text-text-muted uppercase tracking-wider">{label}</label>
              <input value={form[key]} onChange={e => set(key, e.target.value)} className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-text-muted uppercase tracking-wider">Monto USD *</label>
              <input type="number" min="0" step="0.01" value={form.amount_usd} onChange={e => set('amount_usd', e.target.value)} className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-[11px] text-text-muted uppercase tracking-wider">Vencimiento</label>
              <input type="date" value={form.due_date_iso} onChange={e => set('due_date_iso', e.target.value)} className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary !py-2 text-sm">Cancelar</button>
            <button type="submit" className="flex-1 btn-primary !py-2 text-sm">Crear factura</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function PayModal({ invoice, onClose, onPay }) {
  const [amount, setAmount] = useState(invoice.amount_usd);
  const [method, setMethod] = useState('transferencia');
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-card w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-text-heading font-semibold">Registrar pago — {invoice.invoice_id}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-text-muted uppercase tracking-wider">Monto recibido (USD)</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-[11px] text-text-muted uppercase tracking-wider">Método</label>
            <select value={method} onChange={e => setMethod(e.target.value)} className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary">
              {['transferencia', 'tarjeta', 'efectivo', 'yape', 'stripe', 'otro'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 btn-secondary !py-2 text-sm">Cancelar</button>
          <button onClick={() => onPay(parseFloat(amount), method)} className="flex-1 btn-primary !py-2 text-sm flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Confirmar</button>
        </div>
      </motion.div>
    </div>
  );
}

export default function AgenteCobrosPanel({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  const apiFn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFn(`${API}/cobros-agent/invoices`);
      setData(await res.json());
    } catch { toast.error('Error al cargar facturas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAgent = async () => {
    setRunning(true);
    try {
      const res = await apiFn(`${API}/cobros-agent/run`, { method: 'POST' });
      const d = await res.json();
      toast.success(d.message || 'Agente ejecutado');
      await load();
    } catch { toast.error('Error'); }
    finally { setRunning(false); }
  };

  const sendReminder = async (invoiceId) => {
    setRemindingId(invoiceId);
    try {
      const res = await apiFn(`${API}/cobros-agent/remind/${invoiceId}`, { method: 'POST' });
      const d = await res.json();
      if (d.wa_result === 'enviado') toast.success('Recordatorio WA enviado');
      else toast.warning(`WA: ${d.wa_result}`);
      await load();
    } catch { toast.error('Error'); }
    finally { setRemindingId(null); }
  };

  const markPaid = async (invoiceId, paid_amount, method) => {
    try {
      await apiFn(`${API}/cobros-agent/invoices/${invoiceId}/pay`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paid_amount, method }) });
      toast.success('Pago registrado');
      setPayingInvoice(null);
      await load();
    } catch { toast.error('Error'); }
  };

  const createInvoice = async (form) => {
    try {
      await apiFn(`${API}/cobros-agent/invoices`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Factura creada');
      setShowModal(false);
      await load();
    } catch { toast.error('Error'); }
  };

  const invoices = (data?.invoices || []).filter(inv => {
    const matchStatus = filterStatus === 'all' ? true :
      filterStatus === 'vencida' ? isOverdue(inv) :
      inv.status === filterStatus;
    if (!matchStatus) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (inv.client_name || '').toLowerCase().includes(q) ||
           (inv.invoice_id || '').toLowerCase().includes(q) ||
           (inv.concept || '').toLowerCase().includes(q);
  });
  const stats = data?.stats || {};

  return (
    <div className="space-y-5" data-testid="agente-cobros-panel">
      {showModal && <NewInvoiceModal onClose={() => setShowModal(false)} onSave={createInvoice} />}
      <AnimatePresence>{payingInvoice && <PayModal invoice={payingInvoice} onClose={() => setPayingInvoice(null)} onPay={(a, m) => markPaid(payingInvoice.invoice_id, a, m)} />}</AnimatePresence>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" /> Agente de Cobros
          </h3>
          <p className="text-sm text-text-muted mt-0.5">Facturas, recordatorios automáticos por WA y registro de pagos.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="btn-secondary !px-3 !py-2"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={runAgent} disabled={running} className="btn-secondary !px-4 !py-2 text-sm flex items-center gap-1.5">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Cobros automáticos
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary !px-4 !py-2 text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Nueva factura
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total facturas', value: stats.total_invoices ?? 0, color: 'text-primary' },
          { label: 'Pendiente (USD)', value: `$${(stats.total_pending_usd ?? 0).toFixed(2)}`, color: 'text-amber-400' },
          { label: 'Vencidas', value: stats.total_overdue ?? 0, color: 'text-red-400' },
          { label: 'Recaudado (USD)', value: `$${(stats.total_collected_usd ?? 0).toFixed(2)}`, color: 'text-emerald-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="glass-card p-4" data-testid={`cobros-stat-${label}`}>
            <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-xl font-black ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle pb-2">
        <div className="flex gap-1">
          {[['all','Todas'], ['pendiente','Pendientes'], ['vencida','Vencidas'], ['pagado','Pagadas']].map(([id, label]) => (
            <button key={id} onClick={() => setFilterStatus(id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filterStatus === id ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-body'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar factura o cliente…"
            className="pl-9 pr-3 py-1.5 text-sm bg-card border border-border-subtle rounded-xl text-text-body placeholder-text-muted focus:outline-none focus:border-primary"
            data-testid="cobros-search-input"
          />
        </div>
      </div>

      {/* Invoice list */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-text-muted"><Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando…</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-10 text-text-muted text-sm">Sin facturas en este filtro</div>
        ) : (
          invoices.map(inv => {
            const overdue = isOverdue(inv);
            const st = overdue ? STATUS_STYLES.vencida : STATUS_STYLES[inv.status] || STATUS_STYLES.pendiente;
            return (
              <motion.div key={inv.invoice_id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="glass-card p-4 flex flex-wrap items-center gap-4" data-testid={`cobros-invoice-${inv.invoice_id}`}>
                {overdue && <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-text-heading">{inv.client_name}</p>
                  <p className="text-xs text-text-muted">{inv.concept} · Vence: {fmtDate(inv.due_date_iso)}</p>
                  <p className="text-xs text-text-muted">{inv.invoice_id}</p>
                </div>
                <p className="text-lg font-black text-text-heading">${inv.amount_usd?.toFixed(2)}</p>
                <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                {inv.status === 'pendiente' && (
                  <div className="flex gap-2">
                    <button onClick={() => sendReminder(inv.invoice_id)} disabled={remindingId === inv.invoice_id}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 transition-colors disabled:opacity-50"
                      data-testid={`cobros-remind-${inv.invoice_id}`}>
                      {remindingId === inv.invoice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} WA
                    </button>
                    <button onClick={() => setPayingInvoice(inv)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/20 transition-colors" data-testid={`cobros-pay-${inv.invoice_id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Pagar
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
